<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier, vue/html-closing-bracket-newline -->
<script setup>
import { computed, inject, proxyRefs } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'postSale'),
    screen: computed(() => 'detail'),
  })
);

const initials = name =>
  String(name || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const sourceOf = record => record?.source || record || {};
const titleFor = value =>
  String(value || 'Prazo não informado')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const statusTone = bucket => {
  if (bucket === 'vermelhos') return 'border-rose-200 bg-rose-50 text-rose-600';
  if (bucket === 'laranjas' || bucket === 'amarelos') {
    return 'border-amber-200 bg-amber-50 text-amber-600';
  }
  if (bucket === 'em_dia') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return 'border-violet-200 bg-violet-50 text-[#5a24c7]';
};

const metricIcon = label => {
  const value = String(label || '').toLocaleLowerCase('pt-BR');
  if (value.includes('document')) return 'i-lucide-folder';
  if (value.includes('prazo') || value.includes('hoje')) {
    return 'i-lucide-calendar-days';
  }
  if (value.includes('conclu')) return 'i-lucide-check';
  return 'i-lucide-clock-3';
};

const deadlineIcon = deadline => {
  const code = String(deadline.code || '').toLocaleLowerCase('pt-BR');
  if (code.includes('escrit')) return 'i-lucide-pen-line';
  if (code.includes('registro')) return 'i-lucide-file-check-2';
  if (code.includes('vistoria')) return 'i-lucide-search-check';
  if (code.includes('chave')) return 'i-lucide-key-round';
  if (code.includes('credito') || code.includes('financiamento')) {
    return 'i-lucide-landmark';
  }
  if (code.includes('certid')) return 'i-lucide-scroll-text';
  if (code.includes('document')) return 'i-lucide-file-text';
  return 'i-lucide-circle-dot';
};

const stageListFor = source =>
  (Array.isArray(source?.deadlines) ? source.deadlines : []).slice(0, 4).map(
    (deadline, index) => ({
      bucket: deadline.bucket || 'sem_prazo',
      code: deadline.code || `deadline-${index}`,
      dueAt: deadline.due_at,
      icon: deadlineIcon(deadline),
      label: titleFor(deadline.message?.status || deadline.code),
    })
  );

const addressFor = source => {
  const address = source?.transaction?.address || {};
  const line = [address.endereco || address.logradouro, address.numero]
    .filter(Boolean)
    .join(', ');
  const locality = [address.bairro, address.cidade, address.uf]
    .filter(Boolean)
    .join(' · ');
  return [line, address.complemento, locality].filter(Boolean).join(' · ');
};

const source = computed(() => model.detail?.source || {});
const transaction = computed(() => source.value.transaction || {});
const responsible = computed(() => source.value.responsible || null);
const manager = computed(() => source.value.manager || null);
const detailCode = computed(
  () => transaction.value.reference || `#${model.detail?.id || '—'}`
);
const detailTitle = computed(
  () => transaction.value.title || model.detail?.title || 'Processo não retornado'
);
const detailStatus = computed(
  () => source.value.status_label || model.detail?.status || 'Indisponível'
);
const canOpenEditor = computed(
  () => Boolean(model.detail?.id) && model.canEdit
);
const detailAddress = computed(() => addressFor(source.value));
const detailStages = computed(() => stageListFor(source.value));
const nextDeadline = computed(
  () => detailStages.value.find(stage => stage.dueAt) || null
);

const rawRecords = computed(() => {
  const records = Array.isArray(model.records) ? model.records : [];
  const detailId = String(model.detail?.id || '');
  if (detailId && !records.some(record => String(record.id) === detailId)) {
    return [model.detail, ...records];
  }
  return records;
});

const rowFor = (record, index) => {
  const recordSource = sourceOf(record);
  const stages = stageListFor(recordSource);
  const next = stages.find(stage => stage.dueAt) || null;
  return {
    address: addressFor(recordSource),
    code:
      recordSource.transaction?.reference || record.reference || `#${record.id}`,
    id: String(record.id || recordSource.id || `record-${index}`),
    next,
    responsible:
      recordSource.responsible?.name || recordSource.manager?.name || '',
    stages,
    status: recordSource.status_label || record.status || '',
    title:
      record.title || recordSource.transaction?.title || recordSource.title || '',
    unavailable: false,
  };
};

const tableRows = computed(() => {
  const rows = rawRecords.value.slice(0, 5).map(rowFor);
  return [
    ...rows,
    ...Array.from({ length: Math.max(0, 5 - rows.length) }, (_, index) => ({
      id: `unavailable-row-${index}`,
      unavailable: true,
    })),
  ];
});

const metricCards = computed(() => {
  const metrics = Array.isArray(model.metrics) ? model.metrics.slice(0, 4) : [];
  return [
    ...metrics.map((metric, index) => ({
      icon: metric.icon || metricIcon(metric.label),
      id: `${metric.label || 'metric'}-${index}`,
      label: metric.label || 'Métrica',
      value: metric.value || '—',
      unavailable: false,
    })),
    ...Array.from({ length: Math.max(0, 4 - metrics.length) }, (_, index) => ({
      id: `unavailable-metric-${index}`,
      label: 'Métrica indisponível',
      unavailable: true,
      value: '—',
    })),
  ];
});

const client = computed(() => {
  const candidate =
    source.value.client ||
    transaction.value.client ||
    transaction.value.buyer ||
    transaction.value.contact ||
    null;
  return candidate?.name || candidate?.full_name || '';
});

const participantSlots = computed(() => {
  const people = [];
  if (client.value) people.push({ label: 'Cliente', name: client.value });
  if (responsible.value?.name) {
    people.push({ label: 'Responsável', name: responsible.value.name });
  }
  if (manager.value?.name) people.push({ label: 'Gestor', name: manager.value.name });
  return [
    ...people.slice(0, 4),
    ...Array.from({ length: Math.max(0, 4 - people.length) }, (_, index) => ({
      id: `unavailable-person-${index}`,
      unavailable: true,
    })),
  ];
});

const journeySlots = computed(() => {
  const stages = detailStages.value;
  return [
    ...stages,
    ...Array.from({ length: Math.max(0, 4 - stages.length) }, (_, index) => ({
      code: `unavailable-stage-${index}`,
      unavailable: true,
    })),
  ];
});

const eventLabel = event => {
  if (event.reason) return event.reason;
  if (event.from_status_label && event.to_status_label) {
    return `${event.from_status_label} → ${event.to_status_label}`;
  }
  if (event.to_status_label) return `Status atualizado para ${event.to_status_label}`;
  return titleFor(event.event_type || 'Atualização registrada');
};

const activitySlots = computed(() => {
  const loadedHistory = Array.isArray(realData.data?.value?.history)
    ? realData.data.value.history
    : [];
  let events = loadedHistory;
  if (!events.length && Array.isArray(source.value.events)) {
    events = source.value.events;
  }
  const activity = events.slice(0, 3).map((event, index) => ({
    actor: event.actor?.name || event.responsible?.name || 'Sistema',
    date: event.occurred_at || event.date || event.created_at,
    id: event.id || event.sequence || `event-${index}`,
    label: event.label || eventLabel(event),
    unavailable: false,
  }));
  return [
    ...activity,
    ...Array.from({ length: Math.max(0, 3 - activity.length) }, (_, index) => ({
      id: `unavailable-event-${index}`,
      unavailable: true,
    })),
  ];
});

const shownRecords = computed(
  () => rawRecords.value.filter(record => record?.id).length
);
const refreshProcess = () => refresh();
const openEditor = () => {
  if (!canOpenEditor.value) return;

  emit('navigate', { id: model.detail.id, screen: 'edit' });
};
</script>

<template>
  <!-- eslint-disable -->
  <main class="mx-auto w-full max-w-none pb-5 pl-[25px] pr-0 pt-[24px] text-[#101958]">
    <section class="grid grid-cols-[minmax(0,1fr)_443px] items-start gap-6">
      <section class="min-w-0">
        <header data-imudei-visual-id="post-sale-detail-header" class="h-[60px]">
          <h1 class="text-[24px] font-bold tracking-[-0.035em]">Pós-venda</h1>
          <p class="mt-1 text-[10px] text-[#69759c]">Acompanhe cada etapa até a entrega das chaves.</p>
        </header>

        <div data-imudei-visual-id="post-sale-detail-metrics" class="mt-3 grid grid-cols-4 gap-4">
          <article
            v-for="metric in metricCards"
            :key="metric.id"
            class="h-[126px] rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_7px_20px_rgba(38,25,86,0.035)]"
          >
            <div class="flex items-start gap-3">
              <i
                class="grid size-12 place-items-center rounded-full border-2 text-[#5a24c7]"
                :class="metric.unavailable ? 'border-dashed border-[#d9d0e9] bg-[#faf9fd] text-[#a69abf]' : 'border-violet-500 bg-violet-50'"
              >
                <i :class="metric.unavailable ? 'i-lucide-circle-dashed' : metric.icon" class="size-5" />
              </i>
              <span class="min-w-0 pt-1">
                <strong class="block truncate text-[9px]">{{ metric.label }}</strong>
                <b class="mt-1 block text-[20px] leading-5">{{ metric.value }}</b>
                <small class="mt-1 block truncate text-[8px] text-[#69759c]">{{ metric.unavailable ? 'Não retornada pela API' : 'Dados da operação' }}</small>
              </span>
            </div>
            <div class="mt-3 flex -space-x-2">
              <i v-for="avatar in 3" :key="avatar" class="grid size-6 place-items-center rounded-full border-2 border-white bg-[#eee6ff] text-[6px] font-bold text-[#5a24c7]">
                {{ participantSlots[avatar - 1]?.unavailable ? '—' : initials(participantSlots[avatar - 1]?.name) }}
              </i>
            </div>
          </article>
        </div>

        <nav data-imudei-visual-id="post-sale-detail-tabs" class="mt-4 flex h-10 items-end gap-10 border-b border-[#ebe8f1] text-[10px] font-medium">
          <span class="border-b-2 border-[#5a24c7] px-4 pb-3 text-[#5a24c7]">Painel</span>
          <button class="pb-3" type="button" @click="emit('navigate', 'dashboard')">Processos</button>
          <span class="pb-3 text-[#69759c]">Tarefas</span>
        </nav>

        <div class="mt-4 flex h-9 gap-3">
          <span class="flex w-[160px] items-center gap-2 rounded-lg border border-[#e7e2ef] bg-white px-3 text-[8px] text-[#4e5a80]">
            <i class="i-lucide-filter size-3.5 text-[#5a24c7]" />Todas as etapas
            <i class="i-lucide-chevron-down ml-auto size-3" />
          </span>
          <span class="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#e7e2ef] bg-white px-3 text-[8px] text-[#9aa2b9]">
            <i class="i-lucide-search size-3.5 text-[#5a24c7]" />Buscar por imóvel, comprador ou responsável
          </span>
          <button
            class="grid w-10 place-items-center rounded-lg border border-[#e7e2ef] text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            :disabled="loading"
            title="Atualizar dados retornados pela API"
            @click="refreshProcess"
          >
            <i class="i-lucide-refresh-cw size-3.5" :class="loading ? 'animate-spin' : ''" />
          </button>
          <span class="grid w-10 place-items-center rounded-lg border border-[#e7e2ef] text-[#5a24c7]">
            <i class="i-lucide-layout-grid size-3.5" />
          </span>
        </div>

        <article
          v-if="model.localError || error"
          class="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[8px] text-rose-700"
        >
          {{ model.localError || error }}
        </article>

        <div data-imudei-visual-id="post-sale-detail-content" class="mt-4 space-y-2">
          <article
            v-for="row in tableRows"
            :key="row.id"
            class="grid grid-cols-[60px_124px_112px_112px_minmax(142px,1fr)_118px] items-center gap-3 rounded-xl border bg-white px-3 shadow-[0_4px_13px_rgba(38,25,86,0.025)]"
            :class="[
              row.unavailable ? 'h-[96px]' : 'h-[112px]',
              row.unavailable
                ? 'border-dashed border-[#e1dbea] bg-[#fcfbff]'
                : String(row.id) === String(model.detail?.id)
                  ? 'border-[#9b7cff]'
                  : 'border-[#eeeaf5]',
            ]"
          >
            <span class="grid size-[60px] place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-[#202d5e] via-[#5e6ea9] to-[#e8e5f4] text-white">
              <i class="i-lucide-building-2 size-6" :class="row.unavailable ? 'opacity-40' : ''" />
            </span>
            <span class="min-w-0">
              <strong class="block truncate text-[9px]">{{ row.unavailable ? 'Processo indisponível' : row.title }}</strong>
              <small class="mt-1 block truncate text-[8px] text-[#59678f]">{{ row.unavailable ? 'A API não retornou este registro' : row.address || row.code }}</small>
              <small class="mt-1 block text-[8px] text-[#7784a3]">{{ row.unavailable ? '—' : row.code }}</small>
            </span>
            <span class="min-w-0">
              <small class="block text-[7px] text-[#7480a0]">Comprador</small>
              <strong class="mt-2 block truncate text-[8px]">{{ row.unavailable ? 'Não retornado' : 'Não retornado pela API' }}</strong>
            </span>
            <span class="min-w-0">
              <small class="block text-[7px] text-[#7480a0]">Responsável</small>
              <strong class="mt-2 block truncate text-[8px]">{{ row.unavailable ? '—' : row.responsible || 'Não retornado' }}</strong>
            </span>
            <span class="relative flex min-w-0 items-start justify-between pt-2">
              <i class="absolute left-2 right-2 top-[18px] h-px bg-[#d8dceb]" />
              <span v-for="stage in 4" :key="stage" class="relative z-10 flex w-1/4 flex-col items-center text-center">
                <i
                  class="grid size-3 place-items-center rounded-full border-2 border-white"
                  :class="row.stages?.[stage - 1] ? 'bg-[#5a24c7]' : 'bg-[#c9cedd]'"
                >
                  <i v-if="row.stages?.[stage - 1]" class="i-lucide-dot size-2 text-white" />
                </i>
                <small class="mt-2 max-w-12 truncate text-[7px] text-[#65729a]">{{ row.stages?.[stage - 1]?.label || '—' }}</small>
              </span>
            </span>
            <span class="min-w-0">
              <small class="block text-[7px] text-[#7480a0]">Próximo prazo</small>
              <strong class="mt-1 block text-[8px]">{{ row.next?.dueAt ? formatDate(row.next.dueAt) : '—' }}</strong>
              <small class="mt-1 inline-block max-w-full truncate rounded-full bg-violet-50 px-2 py-1 text-[7px] text-[#5a24c7]">{{ row.next?.label || row.status || 'Indisponível' }}</small>
            </span>
          </article>
        </div>

        <footer class="mt-3 flex h-9 items-center justify-between text-[8px] text-[#69759c]">
          <span>Mostrando {{ shownRecords }} de {{ shownRecords }} processos</span>
          <span class="flex h-8 items-center overflow-hidden rounded-lg border border-[#e8e4ef] bg-white">
            <i class="i-lucide-chevron-left grid h-full w-8 place-items-center text-[#9aa2b9]" />
            <b class="grid h-full w-8 place-items-center bg-[#5a24c7] text-white">1</b>
            <i class="i-lucide-chevron-right grid h-full w-8 place-items-center text-[#5a24c7]" />
          </span>
          <span class="flex h-8 items-center gap-3 rounded-lg border border-[#e8e4ef] bg-white px-3">10 por página <i class="i-lucide-chevron-down size-3" /></span>
        </footer>
      </section>

      <aside data-imudei-visual-id="post-sale-detail-sidebar" class="mt-[7px] h-[890px] overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white shadow-[0_8px_24px_rgba(38,25,86,0.04)]">
        <div class="relative h-[137px] overflow-hidden bg-gradient-to-br from-[#1c3151] via-[#607eb9] to-[#f0ae68] text-white">
          <i class="i-lucide-building-2 absolute left-1/2 top-1/2 size-20 -translate-x-1/2 -translate-y-1/2 opacity-90" />
          <i class="absolute -bottom-12 -left-8 size-40 rounded-full bg-[#0a1c34]/25" />
          <i class="absolute -right-10 -top-10 size-36 rounded-full bg-white/10" />
          <i class="absolute right-4 top-4 i-lucide-x size-5" />
        </div>
        <div class="relative -mt-3 h-[764px] rounded-t-2xl bg-white px-4 pb-4 pt-3">
          <div class="relative -top-6">
            <span class="inline-flex rounded-full border px-2 py-1 text-[7px] font-bold" :class="statusTone(source.deadline?.bucket)">{{ detailStatus }}</span>
            <h2 class="mt-2 truncate text-[16px] font-bold">{{ detailTitle }}</h2>
            <p class="mt-1 truncate text-[9px] text-[#59678f]">{{ detailAddress || detailCode }}</p>

            <div class="mt-4 grid grid-cols-2 gap-4">
              <span class="min-w-0">
                <small class="block text-[8px] text-[#7480a0]">Cliente</small>
                <strong class="mt-1 block truncate text-[9px]">{{ client || 'Não retornado pela API' }}</strong>
              </span>
              <span class="min-w-0">
                <small class="block text-[8px] text-[#7480a0]">Responsável</small>
                <strong class="mt-1 block truncate text-[9px]">{{ responsible?.name || manager?.name || 'Não retornado' }}</strong>
              </span>
            </div>
          </div>

          <section class="mt-5">
            <h3 class="text-[10px] font-bold">Jornada do pós-venda</h3>
            <div class="relative mt-3 flex justify-between">
              <i class="absolute left-[12%] right-[12%] top-2 h-px bg-[#cfc6e8]" />
              <span v-for="stage in journeySlots" :key="stage.code" class="relative z-10 flex w-1/4 flex-col items-center text-center">
                <i
                  class="grid size-4 place-items-center rounded-full border-2 border-white"
                  :class="stage.unavailable ? 'bg-[#c9cedd]' : 'bg-[#5a24c7]'"
                >
                  <i v-if="!stage.unavailable" class="i-lucide-dot size-2.5 text-white" />
                </i>
                <small class="mt-2 max-w-16 truncate text-[7px] text-[#59678f]">{{ stage.unavailable ? 'Indisponível' : stage.label }}</small>
              </span>
            </div>
          </section>

          <section class="mt-5 rounded-xl border border-[#eeeaf5] p-3">
            <small class="text-[8px] font-semibold text-[#59678f]">Próxima pendência</small>
            <div class="mt-2 flex items-center gap-2">
              <i class="grid size-7 shrink-0 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]">
                <i :class="nextDeadline ? nextDeadline.icon : 'i-lucide-circle-dashed'" class="size-4" />
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{ nextDeadline?.label || 'Nenhuma pendência retornada' }}</strong>
                <small class="mt-1 block text-[7px] text-[#7480a0]">{{ nextDeadline?.dueAt ? `Prazo: ${formatDate(nextDeadline.dueAt)}` : 'Prazo indisponível' }}</small>
              </span>
              <button
                class="rounded-lg border border-[#e4dafa] px-3 py-2 text-[7px] text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canOpenEditor"
                type="button"
                @click="openEditor"
              >
                Ver detalhes
              </button>
            </div>
          </section>

          <section class="mt-5">
            <h3 class="text-[10px] font-bold">Participantes</h3>
            <div class="mt-3 flex items-start gap-2">
              <span v-for="participant in participantSlots" :key="participant.id || participant.label" class="w-[49px] text-center">
                <i
                  class="mx-auto grid size-8 place-items-center rounded-full border-2 border-white text-[7px] font-bold"
                  :class="participant.unavailable ? 'border-dashed border-[#d9d0e9] bg-[#faf9fd] text-[#a69abf]' : 'bg-[#eee6ff] text-[#5a24c7]'"
                >{{ participant.unavailable ? '—' : initials(participant.name) }}</i>
                <small class="mt-1 block truncate text-[6px]">{{ participant.unavailable ? 'Indisponível' : participant.label }}</small>
              </span>
              <i class="grid size-8 place-items-center rounded-full border border-[#dfe3ef] text-[7px] text-[#59678f]">{{ participantSlots.filter(person => !person.unavailable).length }}</i>
            </div>
          </section>

          <div class="mt-5 grid grid-cols-[minmax(0,1fr)_142px] gap-3">
            <section>
              <h3 class="text-[10px] font-bold">Atividade recente</h3>
              <div class="mt-3 space-y-3">
                <div v-for="event in activitySlots" :key="event.id" class="flex min-h-[38px] gap-2">
                  <i
                    class="grid size-7 shrink-0 place-items-center rounded-full text-[7px] font-bold"
                    :class="event.unavailable ? 'border border-dashed border-[#d9d0e9] bg-[#faf9fd] text-[#a69abf]' : 'bg-[#eee6ff] text-[#5a24c7]'"
                  >{{ event.unavailable ? '—' : initials(event.actor) }}</i>
                  <span class="min-w-0">
                    <strong class="block truncate text-[7px]">{{ event.unavailable ? 'Evento indisponível' : event.actor }}</strong>
                    <small class="mt-1 block line-clamp-2 text-[6px] leading-3 text-[#7480a0]">{{ event.unavailable ? 'A API não retornou outro evento.' : event.label }}</small>
                    <small v-if="!event.unavailable" class="mt-1 block text-[6px] text-[#9aa2b9]">{{ formatDateTime(event.date) }}</small>
                  </span>
                </div>
              </div>
              <button class="mt-2 text-[7px] font-semibold text-[#5a24c7]" type="button" @click="emit('navigate', 'dashboard')">Ver todas as atividades</button>
            </section>

            <section class="space-y-3">
              <article class="rounded-xl border border-dashed border-[#ded5ed] bg-gradient-to-br from-[#fcfbff] to-[#fff9eb] p-3 text-center">
                <small class="text-[7px] font-semibold">Recompensa ao concluir</small>
                <i class="mx-auto mt-3 grid size-10 place-items-center rounded-full bg-amber-100 text-amber-600"><i class="i-lucide-award size-5" /></i>
                <strong class="mt-2 block text-[7px]">Indisponível</strong>
                <small class="mt-1 block text-[6px] text-[#7480a0]">Sem endpoint de recompensa</small>
              </article>
              <article class="rounded-xl border border-[#eeeaf5] p-3">
                <strong class="block text-[7px]">Como você ganha pontos</strong>
                <small class="mt-2 block text-[6px] leading-4 text-[#7480a0]">Pontuação não retornada pela API atual.</small>
                <span class="mt-2 flex items-center gap-1 text-[6px] text-[#5a24c7]"><i class="i-lucide-circle-help size-3" />Sem dados de gamificação</span>
              </article>
            </section>
          </div>

          <footer data-imudei-visual-id="post-sale-detail-footer" class="absolute bottom-4 left-4 right-4 grid grid-cols-[1fr_1.55fr] gap-3">
            <button
              class="flex h-[68px] items-center gap-3 rounded-xl border border-[#cdbaff] bg-[#fbf9ff] px-3 text-left text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!canOpenEditor"
              type="button"
              @click="openEditor"
            >
              <i class="grid size-8 place-items-center rounded-full bg-violet-100"><i class="i-lucide-arrow-right size-4" /></i>
              <span><strong class="block text-[8px]">Próxima etapa</strong><small class="mt-1 block text-[7px] text-[#69759c]">Gerenciar no editor</small></span>
            </button>
            <button
              class="flex h-[68px] items-center gap-3 rounded-xl bg-gradient-to-r from-[#5a24c7] to-[#7029de] px-3 text-left text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!canOpenEditor"
              type="button"
              @click="openEditor"
            >
              <i class="grid size-9 place-items-center rounded-full bg-white/20"><i class="i-lucide-pencil size-4" /></i>
              <span><strong class="block text-[9px]">Editar processo</strong><small class="mt-1 block text-[7px] text-white/80">Abrir editor do processo</small></span>
            </button>
          </footer>
        </div>
      </aside>
    </section>
  </main>
</template>
